import { Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { PENDING_DECISIONS, type PendingDecisionId } from '@/lib/pending-decisions';
import { usePendingDecisionsVisible } from '@/lib/pending-decision-audience';

/**
 * The small `(?)` beside something we decided for you.
 *
 * A sibling of `MockupBanner`: both exist because the one hard rule on this
 * prototype is that a screen must not quietly misrepresent itself.
 * `MockupBanner` says "this data is not real"; this says "this *choice* is not
 * final, and here is what else it could have been".
 *
 * It is deliberately tiny. A decision marker that shouts is worse than no
 * marker, because it makes a working screen look broken and trains people to
 * ignore it. It reads as a footnote — the reader notices it only when they
 * are already wondering about the thing it is attached to, which is exactly
 * the moment the answer is useful.
 *
 * Placement rule: put it on the *element the decision is about*, never in a
 * page header. The point is that Ana, Azalea or Melanie can be looking at the
 * tagline, wonder about the tagline, and get the tagline's decision — not
 * hunt through a page-level list of caveats.
 */

/**
 * #2133 item 7: the aria-label template puts its own full stop after
 * `{{chose}}` ("Currently: {{chose}}. Open to see the alternatives."), and
 * most `chose` values are sentences that already end in one, so every marker
 * read "…saying so.. Open to see…". One closing period is dropped here, never
 * an ellipsis, so the label carries exactly one.
 */
const withoutClosingPeriod = (text: string) => text.trim().replace(/(?<!\.)\.$/, '');

interface PendingDecisionProps {
  /**
   * The decision, or several decisions about ONE control. #2278: two markers
   * side by side on one label read as a stutter; one marker whose popover
   * lists each decision keeps every decision reachable behind one `(?)`.
   */
  id: PendingDecisionId | readonly PendingDecisionId[];
  className?: string;
  /** Which side the popover opens toward. Default 'top' suits footer chrome. */
  side?: 'top' | 'right' | 'bottom' | 'left';
}

export function PendingDecision({ id, className, side = 'top' }: PendingDecisionProps) {
  // #2109 R3 L0: the chrome is in the reader's language; the decision itself
  // (question, chose, because, alternatives) stays English until R4 (R3-B20).
  const { t } = useTranslation('common');
  // Staff only (Mario, 2026-10-14): the popover is written for TRF, and a
  // family met it on their own status page. See `seesPendingDecisions`.
  const visible = usePendingDecisionsVisible();
  const ids: readonly PendingDecisionId[] = typeof id === 'string' ? [id] : id;
  const specs = ids.map((one) => PENDING_DECISIONS[one]);
  const first = specs[0];
  // One label per decision, in order: each already names its question and
  // its current choice, so a screen reader hears every decision behind the mark.
  const ariaLabel = specs
    .map((spec) => t('pendingDecision.ariaLabel', { question: spec.question, chose: withoutClosingPeriod(spec.chose) }))
    .join(' ');

  if (!visible) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={first.ref ? t('pendingDecision.titleWithRef', { ref: first.ref }) : t('pendingDecision.label')}
          aria-label={ariaLabel}
          className={cn(
            // Sized to sit inline with small text without disturbing the line
            // box — an 14px circle on a 16-20px line reads as a footnote mark.
            'inline-flex h-[14px] w-[14px] shrink-0 translate-y-[-1px] items-center justify-center',
            'rounded-full border align-middle',
            'border-[color-mix(in_oklab,var(--stevens-warm)_45%,transparent)]',
            'text-[9px] font-semibold leading-none text-stevens-warm',
            'transition-colors hover:bg-[color-mix(in_oklab,var(--stevens-warm)_14%,transparent)]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stevens-warm/50',
            className,
          )}
        >
          ?
        </button>
      </PopoverTrigger>

      {/* z-[70] (shell stacking contract, ui-ux-guideline §2.10): a marker can
          sit in the z-[60] phone conversations drawer or in a dialog it hosts,
          and a z-50 popover opened behind both. */}
      <PopoverContent side={side} align="start" className="z-[70] w-80 max-h-[70vh] overflow-y-auto">
        <div className="space-y-2.5">
          <p className="m-0 font-label text-label font-medium text-stevens-warm">
            {t('pendingDecision.label')}
          </p>

          {specs.map((spec, i) => (
            <Fragment key={ids[i]}>
              {/* A rule above each decision after the first, so two questions never run together. */}
              <p className={cn('m-0 font-serif text-lede leading-snug text-stevens-ink', i > 0 && 'border-t border-border/50 pt-3')}>
                {spec.question}
              </p>

              <p className="m-0 text-body-sm leading-relaxed text-stevens-ink-2">
                <Trans
                  t={t}
                  i18nKey="pendingDecision.rightNow"
                  values={{ chose: spec.chose, because: spec.because }}
                  components={{ strong: <strong className="font-medium text-stevens-ink" /> }}
                />
              </p>

              <div className="space-y-1">
                <p className="m-0 font-label text-label text-stevens-ink-3">
                  {t('pendingDecision.alsoConsidered')}
                </p>
                <ul className="m-0 list-none space-y-1 p-0">
                  {spec.alternatives.map((alt) => (
                    <li
                      key={alt}
                      className="text-body-sm leading-relaxed text-stevens-ink-3 before:mr-1.5 before:content-['·']"
                    >
                      {alt}
                    </li>
                  ))}
                </ul>
              </div>
            </Fragment>
          ))}

          {/* No form, no thumbs-up. Feedback has one door on this platform
              (the header's Send feedback), and adding a second one here would
              split the record of what TRF asked for across two places. */}
          <p className="m-0 border-t border-border/50 pt-2.5 text-body-sm leading-relaxed text-stevens-ink-3">
            <Trans
              t={t}
              i18nKey="pendingDecision.feedbackInvite"
              components={{ strong: <strong className="font-medium text-stevens-ink-2" /> }}
            />
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
